<script lang="ts">
  import { session, currentStep, records, selectedChecklistId } from '../stores';
  import { checklists } from '../data/checklists';
  import { ROLE_LABELS } from '../types';

  const userSession = $derived($session);
  const checklistId = $derived($selectedChecklistId);
  const currentChecklist = $derived(
    checklists.find(c => c.id === checklistId)
  );
  const currentRecords = $derived($records);

  const completedCount = $derived(
    Object.values(currentRecords).filter(r => r.status === 'ok').length
  );
  const totalCount = $derived(currentChecklist?.items.length || 0);

  let currentTime = $state(new Date().toLocaleString('zh-TW', { hour12: false }));

  function restart() {
    if (confirm('確定要結束並重新開始嗎？內容將會重設。')) {
      session.set(null);
      records.set({});
      selectedChecklistId.set(null);
      currentStep.set('selection');
    }
  }

  // 每秒更新時間
  import { onMount } from 'svelte';
  onMount(() => {
    const interval = setInterval(() => {
      currentTime = new Date().toLocaleString('zh-TW', { hour12: false });
    }, 1000);
    return () => clearInterval(interval);
  });
</script>

<div class="w-full">
  {#if currentChecklist && userSession}
    <div id="screenshot-area" class="bg-white p-6 sm:p-8 rounded-2xl border-3 border-gray-200 shadow-lg mb-8">
      <!-- 標題 -->
      <div class="text-center border-b-4 border-blue-600 pb-6 mb-8">
        <h2 class="text-3xl sm:text-4xl font-black text-gray-900 mb-3">
          ✓ 檢查清單已完成
        </h2>
        <div class="text-2xl sm:text-3xl font-bold text-blue-700 mb-2">
          {currentChecklist.title}
        </div>
        <p class="text-xl sm:text-2xl text-gray-600 font-bold">
          {currentTime}
        </p>
      </div>

      <!-- 人員和角色信息 -->
      <div class="grid grid-cols-2 gap-4 sm:gap-6 mb-8 bg-linear-to-r from-blue-50 to-blue-100 p-6 rounded-2xl border-2 border-blue-300">
        <div>
          <span class="text-gray-600 text-lg font-semibold block mb-1">稱呼</span>
          <span class="text-2xl sm:text-3xl font-bold text-blue-800">
            {userSession.name || ROLE_LABELS[userSession.role]}
          </span>
        </div>
        <div>
          <span class="text-gray-600 text-lg font-semibold block mb-1">角色</span>
          <span class="text-2xl sm:text-3xl font-bold text-blue-800">
            {ROLE_LABELS[userSession.role]}
          </span>
        </div>
      </div>

      <!-- 進度統計 -->
      <div class="mb-8 bg-linear-to-r from-green-50 to-green-100 p-6 rounded-2xl border-3 border-green-400">
        <div class="text-center">
          <div class="text-4xl sm:text-5xl font-black text-green-700 mb-2">
            {completedCount}<span class="text-2xl sm:text-3xl">/</span>{totalCount}
          </div>
          <p class="text-xl sm:text-2xl text-gray-700 font-bold">
            檢查項目完成狀態
          </p>
        </div>
      </div>

      <!-- 詳細清單 -->
      <div class="space-y-3 mb-8">
        <h3 class="text-2xl sm:text-3xl font-bold text-gray-800 border-l-4 border-blue-600 pl-4 mb-4">
          檢查項目清單
        </h3>
        {#each currentChecklist.items as item, idx}
          <div class="flex items-center gap-4 p-4 sm:p-5 rounded-xl border-2"
            class:bg-green-50={currentRecords[item.id]}
            class:border-green-500={currentRecords[item.id]}
            class:bg-red-50={item.required && !currentRecords[item.id]}
            class:border-red-400={item.required && !currentRecords[item.id]}
            class:bg-gray-50={!item.required && !currentRecords[item.id]}
            class:border-gray-300={!item.required && !currentRecords[item.id]}
          >
            <div class="text-center font-bold text-lg sm:text-xl text-gray-600 w-8">
              {idx + 1}.
            </div>
            <div class="flex-1">
              <div class="text-lg sm:text-xl font-bold text-gray-800">
                {item.text}
              </div>
            </div>
            <div class="text-center min-w-20">
              {#if currentRecords[item.id]}
                <div class="bg-green-500 text-white px-4 py-2 rounded-lg font-bold text-lg sm:text-xl">
                  ✓ OK
                </div>
              {:else if item.required}
                <div class="bg-red-500 text-white px-4 py-2 rounded-lg font-bold text-lg">
                  ✗ 未完成
                </div>
              {:else}
                <div class="bg-gray-400 text-white px-4 py-2 rounded-lg font-bold text-lg">
                  - 未勾選
                </div>
              {/if}
            </div>
          </div>
        {/each}
      </div>

      <!-- 底部簽名區 -->
      <div class="mt-8 pt-6 border-t-4 border-gray-300 text-center">
        <p class="text-gray-600 text-lg font-semibold mb-4">檢查人員簽名：____________________</p>
        <p class="text-gray-400 text-sm font-bold italic">-- 報告結束 --</p>
      </div>
    </div>

    <!-- 工具按鈕 -->
    <div class="space-y-4 pb-8">
      <p class="text-center text-lg sm:text-xl text-blue-700 font-bold">
        📷 建議進行螢幕截圖並儲存或分享
      </p>

      <button
        onclick={restart}
        class="w-full bg-linear-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white p-6 sm:p-8 rounded-2xl text-2xl sm:text-3xl font-bold shadow-lg active:scale-95 transition-all"
      >
        ↻ 回首頁重新開始
      </button>
    </div>
  {/if}
</div>
